import Locale from "../../utils/Locale";
import PoolDevice from "./base";
import * as Types from "../Types";
import { ValueAndUnit } from "../../types";


/** Base class, for devices with On/Off capability */
export default abstract class PoolDeviceOnOffable extends PoolDevice {
    static isOnOffable = true;

    get isOnOffable(): boolean {
        return (this.constructor as typeof PoolDeviceOnOffable).isOnOffable;
    }
    
    /** Identifier of the CriticalField that holds the on/off value of the device */
    OnOffIdentifier: string;

    getCriticalFieldOnOffPropertyAndValue(): Types.PoolDeviceCriticalFieldPropertyAndValue {
        return this.getCriticalFieldPropertyAndValue(this.OnOffIdentifier);
    }

    setCriticalFieldOnOff(isOn: boolean) {
        this.setCriticalField(this.OnOffIdentifier, isOn ?
            Types.PoolsideDaemonDeviceStateValues.ON :
            Types.PoolsideDaemonDeviceStateValues.OFF
        );
    }

    getCriticalFieldDiffForDisplay(CriticalFieldsPropertyAndValue: Types.PoolDeviceCriticalFieldPropertyAndValue[]) {
        // If actual on/off state is ON then show diff of all critical fields
        // If actual on/off state is OFF or UNKNOWN then only show the diff of the on/off field
        const isOnActual = this.isOnActual();
        return super.getCriticalFieldDiffForDisplay(CriticalFieldsPropertyAndValue)
            .filter(item => (isOnActual || item === this.OnOffIdentifier))
        ;
    }

    /* Commented out. Decided to always show the information fields. Otherwise the UI would "keep jumping" when powering on/off devices.
    getInformationFieldsFromStateOrInternalForDisplay(): Types.PoolDevicePropertyToDisplay[] {
        const fields = super.getInformationFieldsFromStateOrInternalForDisplay();
        if (this.isOnActual()) {
            return fields;
        }
        return [];
    }
    */

    getInformationFieldsForDisplay(informationFields: Types.PoolDevicePropertyToDisplay[]): Types.PoolDevicePropertyAndValue[] {
        return informationFields?.map(itemInformationFields => {
            let state = {} as { value: any, updated: Date };
            if (this.isOnActual() || itemInformationFields.DisplayWhenOff) {
                state = this.getDeviceState(itemInformationFields.Name);
            }
            const valueAndUnitDesreid = this.formatDeviceStatus(
                itemInformationFields.DisplayProcessingLogic,
                state?.value
            );
            return {
                Property: itemInformationFields,
                Value: state?.value,
                ValueAndUnit: valueAndUnitDesreid,
                ValueFormatted: (valueAndUnitDesreid.value ?? "") + (valueAndUnitDesreid.separator ?? "") + (valueAndUnitDesreid.unit ?? ""),
                Updated: state?.updated
            };
        }) || []
    }

    getCriticalFieldSummary(mode: Types.criticalFieldSummaryModes): string {
        if (this.isWinterized()) {
            return Locale.gettext("Winterized");
        }

        switch (mode) {
        case Types.criticalFieldSummaryModes.FULL_IF_ON_OTHERWISE_ONOFF_ONLY: {
            // Get full short status only if turned on.
            // Get just on/off otherwise
            if (this.isOnActual()) {
                return this.getCriticalFieldSummaryCustom();
            } else {
                const onOffField = this.getCriticalFieldOnOffPropertyAndValue();
                return onOffField?.ActualPropertyAndValue?.ValueFormatted;   
            }
        }
        case Types.criticalFieldSummaryModes.FULL:
        default:
            // Get full short status
            return this.getCriticalFieldSummaryCustom();
        }
    }


    /**
     * @returns True: on. False: off. Undefined: unknown
     */
    isOnActual() {
        const onOffField = this.getCriticalFieldOnOffPropertyAndValue();
        const value = onOffField?.ActualPropertyAndValue?.Value;
        if (value === Types.PoolsideDaemonDeviceStateValues.ON) {
            return true;
        } else if (value === Types.PoolsideDaemonDeviceStateValues.OFF) {
            return false;
        }
        return;
    }

    /**
     * @returns True: on. False: off. Undefined: unknown
     */
    isOnDesired() {
        const onOffField = this.getCriticalFieldOnOffPropertyAndValue();
        const value = onOffField?.DesiredPropertyAndValue?.Value;
        if (value === Types.PoolsideDaemonDeviceStateValues.ON) {
            return true;
        } else if (value === Types.PoolsideDaemonDeviceStateValues.OFF) {
            return false;
        }
        return;
    }

    /**
     * @returns True: on. False: off. Undefined: unknown
     */
    isOnLocal() {
        const onOffField = this.getCriticalFieldOnOffPropertyAndValue();
        const value = onOffField?.LocalDesiredPropertyAndValue?.Value;
        if (value === Types.PoolsideDaemonDeviceStateValues.ON) {
            return true;
        } else if (value === Types.PoolsideDaemonDeviceStateValues.OFF) {
            return false;
        }
        return;
    }

    isActive(): boolean {
        return this.isOnActual() || this.isOnDesired();
    }
}

